import _ from 'lodash';
import {openDB, deleteDB, wrap, unwrap} from 'idb';
import syncConfig from '../offline/sync.config.js';
import stringHash from 'string-hash';
import {serviceURL} from '../util/uiconstants';
import env from '../util/env';

/*
 * This is application specific Middleware and will process only GET_ALL_TASKS action
 * Responsibilities:
 * If app is offline, update the api url to remove the pagination in querystring and include only category of allTasks(2)
 * If app is online, then get tasklist from idb. If empty create with default values
 */
const offlineResponse = (config) => (store) => (next) => (action) => {
	const __NAME = 'Offline Response Middleware',
		UPDATE_APP_ONLINE_STATE = 'UPDATE_APP_O	NLINE_STATE',
		API_ERRORS = 'API_ERRORS';

	if (action.type === UPDATE_APP_ONLINE_STATE && action.meta && action.meta.offlinemonitor) {
		action.type = 'SET_CONNECTIVITY_STATE';
		return next(action);
	}

	if (!action.mergeOffline) {
		return next(action);
	}

	const appOnlineState = store.getState().apponlinestate;
	if (_.isBoolean(appOnlineState) && appOnlineState) {
		return next(action);
	}

	if (!(action.meta && action.meta.api)) {
		return next(action);
	}

	// skip to next middleware for API_ERROR action
	if (action.type === API_ERRORS) {
		return next(action);
	}

	const ONLINE_DB = 'ONLINE_DB',
		OFFLINE_DB = 'OFFLINE_DB';

	const dbPromise = openDB('canvas-idb', 1, {
		upgrade(db) {
			db.createObjectStore(ONLINE_DB);
			db.createObjectStore(OFFLINE_DB);
		}
	});

	const idbKeyval = {
		async get(key) {
			return (await dbPromise).get(ONLINE_DB, key);
		},
		async getOffline(key) {
			return (await dbPromise).get(OFFLINE_DB, key);
		},
		async set(key, val) {
			return (await dbPromise).put(ONLINE_DB, val, key);
		},
		async setOffline(key, val) {
			return (await dbPromise).put(OFFLINE_DB, val, key);
		},
		async delete(key) {
			return (await dbPromise).delete(ONLINE_DB, key);
		},
		async clear() {
			return (await dbPromise).clear(ONLINE_DB);
		},
		async keys() {
			return (await dbPromise).getAllKeys(ONLINE_DB);
		}
	};

	if (!config) {
		const configMsg = `
		Config unavailable.
		The AD package requires config to get the authorization token for the api call.`;
		console.log(configMsg);
	}

	async function approvals(evidences, newURL) {
		return await Promise.all(
			evidences.map(async (evidence) => {
				let signoffUrl = newURL.split('TaskDocuments')[0] + 'Documents/' + evidence.id + '/Approvals';
				let signoffPostHash = stringHash(signoffUrl.toLowerCase() + 'POST');
				let signoffDeleteHash = stringHash(signoffUrl.toLowerCase() + 'DELETE');
				console.log('Approval offline binding');

				let removedApprovals = [];
				// Get Already removed signoff items.
				await idbKeyval.getOffline(signoffDeleteHash).then((dh) => {
					if (dh) {
						removedApprovals = dh.map((sd) => {
							return sd.meta.api.data.approvalId;
						});
					}
				});

				let newApprovals = [...evidence.approvals];
				//get the sign off approval from offline.
				await idbKeyval.getOffline(signoffPostHash).then((d) => {
					if (d) {
						d.map((sd) => {
							// If already removed, then dont add the list to approvals.
							var approval = {
								approvalUserId: sd.response.approvalUserId,
								approvalUserInititals: sd.response.userInitials,
								approvalUserName: sd.response.name,
								createDate: sd.response.createDate,
								id: sd.response.approvalId,
								isPreparer: sd.response.isPreparer
							};
							newApprovals.push(approval);
						});
					}
				});

				let filteredApproval = [...newApprovals];
				//Remove the offline removed item from approval.
				if (removedApprovals && removedApprovals.length) {
					filteredApproval = newApprovals.filter(function(item) {
						return !removedApprovals.includes(item.id);
					});
				}
				evidence.approvals = [...filteredApproval];
				return evidence;
			})
		).then((values) => {
			//action.response[item.updateEntity] = Object.assign([], values);

			return values;
		});
	}

	async function evidences(evidences, newURL) {
		let _eid = stringHash(newURL.toLowerCase() + 'POST');
		let _reid = stringHash(newURL.toLowerCase() + 'DELETE');
		console.log('evidences offline binding');

		await idbKeyval.getOffline(_eid).then((dp) => {
			if (dp) {
				let new_evidences = dp.map((d) => {
					var e_data = d.meta.api.data;
					return {
						id: e_data.DocumentId,
						documentName: e_data.DocumentName.split('.' + e_data.Documentfileext)[0],
						fileExt: e_data.Documentfileext,
						documentTypeId: e_data.DocumentTypeId,
						sourceEngagementId: e_data.SourceEngagementID,
						engagementId: e_data.EngagementId,
						approvals: e_data.approvals,
						documentReviews: [],
						clientApprovals: [],
						gaComponentDocumentStatusID: null
					};
				});
				evidences = [...evidences, ...new_evidences];
			}
		});

		await idbKeyval.getOffline(_reid).then((dh) => {
			if (dh) {
				let remove_evidences = dh.map((sd) => {
					return sd.meta.api.data.DocumentName + '.' + sd.meta.api.data.Documentfileext;
				});
				evidences = evidences.filter(function(item) {
					return !remove_evidences.includes(item.documentName + '.' + item.fileExt);
				});
			}
		});
		return evidences;
	}

	//get the action value from sync.config
	let offlineRecords = syncConfig[action.type];
	if (offlineRecords && offlineRecords.length > 0) {
		//update the id in urls for offlineDB
		let newURL = offlineRecords[0].url.replace(/{\w+}/g, function(all) {
			all = all.substring(1, all.length - 1);
			return action.response[all] || action[all] || '';
		});

		console.log('new url:', newURL);

		if (action.engagementId) {
			newURL = env.getBaseURL(serviceURL, action.engagementId) + newURL;
		} else {
			//In task list enaggement id is coming as empty
			newURL = action.meta.api.url.split(newURL)[0] + newURL;
		}
		console.log('Offline Response after replacing ID', newURL);

		offlineRecords.forEach((item, index, array) => {
			let _id;
			if (item.path) {
				_id = stringHash(newURL.toLowerCase() + item.method + item.path);
			} else {
				_id = stringHash(newURL.toLowerCase() + item.method);
			}

			console.log('_id', _id);

			// get the record from offlineDB using the hash
			idbKeyval.getOffline(_id).then((offlineactions) => {
				if (offlineactions) {
					if (item.updateEntity) {
						if (item.updateEntity !== 'data') {
							action.response[item.updateEntity] = offlineactions.meta.api.data[0].value;
						}
					} else {
						console.log('Update Entity not present : ', offlineactions.meta.api.data);
						action.response = offlineactions.meta.api.data[0].value;
					}
				}
				return next(action);
			});

			if (item.updateEntity && item.updateEntity === 'data') {
				let taskevidences = !_.isEmpty(action.response) ? _.cloneDeep([...action.response[item.updateEntity]]) : [];
				if (offlineRecords[0].filterEntity && action[offlineRecords[0].filterEntity]) {
					newURL = newURL + '_' + action[offlineRecords[0].filterEntity];
				}
				evidences(taskevidences, newURL)
					.then((docs) => {
						console.log('offline response evidence call return: ', docs);
						approvals(docs, newURL)
							.then((finaldocs) => {
								console.log('offline response approval call return: ', finaldocs);

								action.response[item.updateEntity] = Object.assign([], finaldocs);
								return next(action);
							})
							.catch((error) => {
								action.error = error;
								action.response = {};
								action.responseObj = {};
								return next({type: API_ERRORS, error});
							});
					})
					.catch((error) => {
						action.error = error;
						action.response = {};
						action.responseObj = {};
						return next({type: API_ERRORS, error});
					});
			}
		});
	} else {
		return next(action);
		//return resolve('Offline Repsonse - no offline records in sync config');
	}
};
export default offlineResponse;
